import { Box, ScrollArea } from "@mantine/core"
import { ErrorBox, Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import {
  useFindTraceabilityGraphByProductId,
  useFindOperationTreeByProductId,
} from "~/gen/api"
import { useFindFeatureFlagByName } from "~/hooks/useFindFeatureFlagByName"
import { MULTISOURCE_FEATURE_FLAG_NAME } from "~/pages/SupplierPortal/TraceabilityRequests/constants/multisource.constants"
import { useGetOperations } from "~/utils/operations"

import { ProductTraceabilityTree } from "./ProductTraceabilityTree"
import { getMainItem, getNodesById } from "./product-traceability.utils"

const TRACEABILITY_PANEL_MIN_HEIGHT = 240

type Props = {
  product: LibraryResponses.ProductDetails
}

export const ProductTraceability = ({ product }: Props) => {
  const { t } = useTranslation("traceability")
  const { t: tCommon } = useTranslation()
  const featureFlagQuery = useFindFeatureFlagByName(
    MULTISOURCE_FEATURE_FLAG_NAME
  )
  const operationsByName = useGetOperations()
  const isFeatureFlagResolved = featureFlagQuery.isSuccess
  const isMultisourceEnabled = featureFlagQuery.data?.data?.state === true
  const shouldUseMultisourceGraph =
    isFeatureFlagResolved && isMultisourceEnabled
  const shouldUseLegacyTree = isFeatureFlagResolved && !isMultisourceEnabled

  const operationTreeQuery = useFindOperationTreeByProductId(product.uuid, {
    query: {
      enabled: shouldUseLegacyTree,
    },
  })

  const graphQuery = useFindTraceabilityGraphByProductId(product.uuid, {
    query: {
      enabled: shouldUseMultisourceGraph,
    },
  })

  if (featureFlagQuery.isError) {
    return (
      <ErrorBox
        title={tCommon("dataLoad.error.title")}
        error={featureFlagQuery.error}
      />
    )
  }

  if (shouldUseMultisourceGraph && graphQuery.isError) {
    return (
      <ErrorBox
        title={tCommon("dataLoad.error.title")}
        error={graphQuery.error}
      />
    )
  }

  if (shouldUseLegacyTree && operationTreeQuery.isError) {
    return (
      <ErrorBox
        title={tCommon("dataLoad.error.title")}
        error={operationTreeQuery.error}
      />
    )
  }

  const isActiveQueryPending =
    !isFeatureFlagResolved ||
    (shouldUseMultisourceGraph
      ? graphQuery.isPending
      : operationTreeQuery.isPending)

  if (isActiveQueryPending) {
    return (
      <Box mih={TRACEABILITY_PANEL_MIN_HEIGHT}>
        <Loader fullPage />
      </Box>
    )
  }

  const nodesById = getNodesById({
    isMultisourceEnabled,
    operationNodes: operationTreeQuery.data?.data?.nodes,
    graphNodes: graphQuery.data?.data?.nodes,
  })

  const mainItem = getMainItem({
    isMultisourceEnabled,
    nodesById,
    graphRootId: graphQuery.data?.data?.rootId,
  })

  if (mainItem === undefined) {
    return null
  }

  return (
    <ScrollArea w="100%" type="scroll" offsetScrollbars>
      <ProductTraceabilityTree
        item={mainItem}
        nodesById={nodesById}
        isFirstChild={false}
        ancestorReferences={[]}
        translateItemType={(type) => t(`itemType.${type}`)}
        translateActivity={(activityName) =>
          operationsByName[activityName]?.name ?? activityName
        }
      />
    </ScrollArea>
  )
}
